<template>
    <div class="w-full h-[90%] flex flex-col font-normal text-gray-700 tracking-normal justify-start items-start overflow-y-scroll p-2">
        <div class="w-full flex flex-col space-y-2">
            <div class="text-sm pb-1 border-b border-gray-200">Editing Reported Details & CRM Status</div>
            <div class="text-xs w-full">
                <ul class="list-disc list-inside w-full pretty pb-2">
                    <li>CRM Statuses are calculated from a company's reported detail.</li>
                    <li>Reported details are auto-computed for each company by default.</li>
                    <li>The detail that is furthest along the CRM workflow is the auto-compute selection.</li>
                    <li>You can manually select a different detail to report off of. This will also change the company's CRM status.</li>
                    <li>To manually select a CRM detail, click on the company row to expand and select a detail.</li>
                    <li>Changes are tracked along with progress update updates. Click the save button in the top right to save any changes made to reported details.</li>
                </ul>
            </div>
        </div>
        <div class="w-full flex flex-col space-y-2 pb-2">
            <div class="text-sm pb-1 border-b border-gray-200">CRM Details to CRM Status Mapping</div>
            <div class="text-xs w-full">
                <div class="pb-2">The below CRM Statuses will be calculated in the following priority, via corresponding CRM details:</div>
                <div class="w-[500px]">
                    <table class="table-fixed">
                        <thead>
                            <tr>
                                <th class="border-t border-b border-l border-gray-400 h-full text-xs whitespace-nowrap w-5 text-left"></th>
                                <th class="border-t border-b border-gray-400 h-full text-xs whitespace-nowrap w-[250px] text-left px-3 py-1.5">Computed CRM Status</th>
                                <th class="border-t border-b border-r border-gray-400 h-full text-xs whitespace-nowrap w-[250px] text-left px-3 py-1.5">CRM Detail Type</th>
                            </tr>
                        </thead>
                        <tbody>
                            <crm-status-map-row 
                                v-for="(crmStatusMap, index) in crmStatusMaps" 
                                :crm-status-mapping="crmStatusMap" 
                                :key="index" 
                                :index="index"
                            />  
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
        <div class="w-full flex flex-col space-y-2">
            <div class="text-sm pb-1 border-b border-gray-200">Adding Progress Updates</div>
            <div class="text-xs">
                <ul class="list-disc list-inside w-full pretty pb-2">
                    <li>To add view updates, toggle the <span class="font-semibold">View Progress Updates</span> switch at the top of the page.</li>
                    <li>The right side of the page displays six buckets; each section corresopnds to a section on the CRM report.</li>
                    <li>Progress updates are automatically sorted into each bucket based off of CRM Status</li>
                    <li>To add a progress update for a company, click the insert button in the rightmost cell of the target list row.</li>
                    <li>To remove a progress update, click the X button in the top right of the progress update card.</li>
                    <li>To add progress updates for all companies, click the <span class="font-semibold">Add All</span> button at the top of the page.</li>
                    <li>To remove all progress updates, click the <span class="font-semibold">Remove All</span> button at the top of the page.</li>
                    <li>To add notes to a progress update, type any text into the progress update card. Add an asterisk <span class="font-semibold">*</span> to add a bulletpoint,</li>
                    <li>Select the Bold On Report checkbox to display the progress update in bold on the CRM Report.</li>
                    <li>To bold or unbold all progress updates, click <span class="font-semibold">Bold All</span> or <span class="font-semibold">Unbold All</span> at the top of the page.</li>
                    <li>Changes are tracked along with reported details updates. Click the save button in the top right to save any changes made to reported details.</li>
                </ul>
            </div>
        </div>
        <div class="w-full flex flex-col space-y-2">
            <div class="text-sm pb-1 border-b border-gray-200">Publishing & Printing A Report</div>
            <div class="text-xs">
                <ul class="list-disc list-inside w-full pretty pb-2">
                    <li>To print a CRM report, it needs to be published first.</li>
                    <li>To publish a report, click the save button in the top right corner of the page.</li>
                    <li>Select <span class="font-semibold">Save & Publish</span>.</li>
                    <li>Note: You can save changes as an unpublished draft by right-clicking the save button or clicking and selecting 'Save Draft'.</li>
                    <li>Name the report and save</li>
                    <li>Select <span class="font-semibold">Print View</span> tab.</li>
                    <li>The most recent published report is automatically selected.</li>
                    <li>Click <span class="font-semibold">Preview Report</span>.</li>
                    <li>Review the report and click the print button to view as a PDF and print.</li>

                </ul>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import TableCell from '../base-components/TableCell.vue';
import CrmStatusMapRow from './CrmStatusMapRow.vue';

const crmStatusMaps = ref([
    {
        crmStatus: 'Dead',
        crmDetails: [
            'Dead - Out Of Business',
            'Dead - Acquired',
            'Other: Dead'
        ],
        rowStyle: 'background-color: #4B5563',
    },
    {
        crmStatus: 'Client Pass',
        crmDetails: [
            'Client Passed',
        ],
        rowStyle: 'background-color: #4B5563',
    },
    {
        crmStatus: 'Not Interested',
        crmDetails: [
            'Target Passed'
        ],
        rowStyle: 'background-color: #4B5563',
    },
    {
        crmStatus: 'Transaction Closed',
        crmDetails: [
            'Transaction Closed'
        ],
        rowStyle: 'background-color: #0F102E;',
    },
    {
        crmStatus: 'Revisit / Cut',
        crmDetails: [
            'Revisit / Cut'
        ],
        rowStyle: 'background-color: #FCD34D;',
    },
    {
        crmStatus: 'Not Now / Keep Warm',
        crmDetails: [
            'Not Now / Keep Warm'
        ],
        rowStyle: 'background-color: #FCD34D;',
    },
    {
        crmStatus: 'Under LOI',
        crmDetails: [
            'LOI Executed'
        ],
        rowStyle: 'background-color: #282B7B;',
    },
    {
        crmStatus: 'IOI Submitted',
        crmDetails: [
            'IOI Submitted'
        ],
        rowStyle: 'background-color: #3C3F7C;',
    },
    {
        crmStatus: 'Active - In Dialogue',
        crmDetails: [
            'LOI Submitted',
            'Active High Priority',
            'Received Materials',
            'Preliminary Info Request Sent',
            'NDA Executed',
            'NDA Sent for Review ',
            'Follow up on DQ',
            'Follow up on NDA',
            'In-Person Client Meeting / Facility Tour',
            'CLient Call/Zoom',
            'Shedd Call/Zoom',
            'Schedule In-Person Meeting',
            'Schedule Client Call/Zoom',
            'Schedule Shedd Call/Zoom',
            'Additional Response / Target Reached Back Out',
            'Initial Response',
            'Other: Active - In Dialogue'
        ],
        rowStyle: 'background-color: #8489C2;',
    },
    {
        crmStatus: 'Responded / Coverage',
        crmDetails: [
            'Responded Coverage',
            'Cold Call Success'
        ],
        rowStyle: 'background-color: #FCD34D;',
    },
    {
        crmStatus: 'No Response',
        crmDetails: [
            'LinkedIn DM',
            'Sent Text to Mobile',
            'Cold Call Attempt',
            'Email 4',
            'Email 3',
            'Email 2',
            'Email 1',
            'Email Sent',
            'Other: No Response',
        ],
        rowStyle: 'background-color: #C7D2FE;',
    },
    {
        crmStatus: 'Not Contacted (Or Unmapped)',
        crmDetails: [
            'Other: Not Contacted',
            'No CRM Details',
            'Next Step Needed (Unmapped)',
            'Hold (Unmapped)',
            'Other (Unmapped)',
        ],
        rowStyle: 'background-color: #6B7280;',
    },
])
</script>